import { useEffect, useMemo, useState } from 'react';
import { useAnalysisData } from '../../contexts/AnalysisDataContext';
import { AnalysisRange } from './useAnalysisRange';
import { Granularity } from '../../utils/analytics/types';
import {
  churnRiskGuests,
  partyComposition,
  repeatGuestRate,
  repeatVsNewSeries,
  rfmSegments,
  segmentSummary,
  tenureCohorts,
} from '../../utils/analytics/memberMetrics';
import {
  fetchReviewsSummary,
  fetchRvDemographics,
  ReviewsSummary,
  RvDemographics,
} from '../../services/saasAnalyticsService';
import { getTodayString } from '../../utils/dateUtils';

export function useMemberAnalysis(range: AnalysisRange, granularity: Granularity) {
  const { stays, members, loading, error, ensureRange } = useAnalysisData();

  // Phase 3 backend aggregates from hcp-saas (403 → null → panels hidden)
  const [reviews, setReviews] = useState<ReviewsSummary | null>(null);
  const [rvDemographics, setRvDemographics] = useState<RvDemographics | null>(null);
  const [aggregatesLoading, setAggregatesLoading] = useState(true);

  useEffect(() => {
    ensureRange(range.from, range.to);
  }, [range.from, range.to, ensureRange]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchReviewsSummary(), fetchRvDemographics()])
      .then(([reviewsResult, rvResult]) => {
        if (cancelled) return;
        setReviews(reviewsResult);
        setRvDemographics(rvResult);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAggregatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const derived = useMemo(() => {
    const { from, to } = range;
    const today = getTodayString();
    const guests = rfmSegments(stays, today);
    const memberLites = members.map(m => ({
      id: m.id,
      name: `${m.first_name} ${m.last_name}`.trim() || m.username,
      createdAt: m.created_at,
    }));
    const nameByMemberId = new Map(memberLites.map(m => [m.id, m.name]));

    return {
      loading,
      error,
      totalMembers: members.length,
      repeatRate: repeatGuestRate(stays, from, to),
      repeatSeries: repeatVsNewSeries(stays, from, to, granularity),
      guests,
      segments: segmentSummary(guests),
      churnRisk: churnRiskGuests(stays, today),
      cohorts: tenureCohorts(memberLites, stays),
      party: partyComposition(stays, from, to),
      nameByMemberId,
    };
  }, [stays, members, range, granularity, loading, error]);

  return { ...derived, reviews, rvDemographics, aggregatesLoading };
}
